import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { NAV_ITEMS, SITE } from "@/lib/site";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

export default function SiteHeader({ transparent = false }) {
    const [scrolled, setScrolled] = useState(false);
    const [dropOpen, setDropOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobilePresta, setMobilePresta] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setDropOpen(false);
        setMobileOpen(false);
        setMobilePresta(false);
    }, [location.pathname]);

    const isFilled = scrolled || !transparent;

    return (
        <header
            data-testid="site-header"
            className={cn(
                "fixed top-0 inset-x-0 z-50 transition-all duration-300",
                isFilled
                    ? "bg-white/95 backdrop-blur border-b border-concrete-200 shadow-[0_1px_0_rgba(4,12,80,0.04)]"
                    : "bg-transparent",
            )}
        >
            <div className="rcps-container flex items-center justify-between h-[80px]">
                <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 focus-ring">
                    <img
                        src={isFilled ? SITE.logos.blue : SITE.logos.white}
                        alt="RCPS Terrassement"
                        className="h-10 md:h-11 w-auto"
                    />
                </Link>

                {/* Desktop nav */}
                <nav data-testid="header-navigation" className="hidden lg:flex items-center gap-1">
                    {NAV_ITEMS.map((item) =>
                        item.children ? (
                            <div
                                key={item.label}
                                className="relative"
                                onMouseEnter={() => setDropOpen(true)}
                                onMouseLeave={() => setDropOpen(false)}
                            >
                                <button
                                    data-testid="header-prestations-trigger"
                                    className={cn(
                                        "px-4 py-2 text-sm font-medium tracking-tight flex items-center gap-1.5 rounded transition whitespace-nowrap",
                                        isFilled
                                            ? "text-navy-700 hover:bg-concrete-100"
                                            : "text-white hover:bg-white/10",
                                    )}
                                    aria-expanded={dropOpen}
                                    onClick={() => setDropOpen((v) => !v)}
                                >
                                    {item.label}
                                    <ChevronDown className={cn("w-4 h-4 transition-transform", dropOpen && "rotate-180")} />
                                </button>
                                {dropOpen && (
                                    <div
                                        data-testid="header-prestations-panel"
                                        className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[560px]"
                                    >
                                        <div className="bg-white border border-concrete-200 rounded-md shadow-[0_20px_50px_rgba(2,6,23,0.10)] p-3 grid grid-cols-1 gap-1">
                                            <div className="px-3 py-2 border-b border-concrete-100 mb-1">
                                                <p className="kicker text-navy-700">Prestations</p>
                                                <p className="text-navy-800 font-heading text-base mt-1">
                                                    Des travaux de terrain, d’infrastructure et d’aménagement extérieur à Genève et en Suisse romande.
                                                </p>
                                            </div>
                                            {item.children.map((c, idx) => (
                                                <NavLink
                                                    key={c.to}
                                                    to={c.to}
                                                    data-testid={`header-prestations-link-${idx}`}
                                                    className={({ isActive }) =>
                                                        cn(
                                                            "group flex items-start gap-3 px-3 py-3 rounded transition",
                                                            isActive
                                                                ? "bg-concrete-100 text-navy-700"
                                                                : "text-navy-800 hover:bg-concrete-100",
                                                        )
                                                    }
                                                >
                                                    <span className="font-mono-num text-[11px] text-steel mt-1">
                                                        {String(idx + 1).padStart(2, "0")}
                                                    </span>
                                                    <span className="flex-1">
                                                        <span className="block text-sm font-semibold">{c.label}</span>
                                                    </span>
                                                    <span className="opacity-0 group-hover:opacity-100 transition text-navy-700">→</span>
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`header-nav-${item.to.replace(/\//g, "-")}`}
                                end={item.to === "/"}
                                className={({ isActive }) =>
                                    cn(
                                        "px-4 py-2 text-sm font-medium tracking-tight rounded transition whitespace-nowrap",
                                        isFilled
                                            ? isActive
                                                ? "text-navy-700 bg-concrete-100"
                                                : "text-navy-700 hover:bg-concrete-100"
                                            : isActive
                                              ? "text-white bg-white/15"
                                              : "text-white hover:bg-white/10",
                                    )
                                }
                            >
                                {item.label}
                            </NavLink>
                        ),
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    <a
                        href={`tel:${SITE.phoneTel}`}
                        data-testid="header-phone-link"
                        className={cn(
                            "text-sm font-medium flex items-center gap-2 px-3 py-2 rounded transition whitespace-nowrap",
                            isFilled ? "text-navy-700 hover:bg-concrete-100" : "text-white hover:bg-white/10",
                        )}
                    >
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        {SITE.phone}
                    </a>
                    <Link to="/contact" data-testid="header-quote-cta">
                        <Button
                            className={cn(
                                "h-10 px-5 rounded-md font-semibold",
                                isFilled
                                    ? "bg-navy-700 hover:bg-navy-800 text-white"
                                    : "bg-white hover:bg-concrete-100 text-navy-700",
                            )}
                        >
                            Demander un devis
                        </Button>
                    </Link>
                </div>

                {/* Mobile trigger */}
                <div className="lg:hidden flex items-center gap-2">
                    <a
                        href={`tel:${SITE.phoneTel}`}
                        aria-label="Appeler RCPS"
                        className={cn(
                            "p-2 rounded transition",
                            isFilled ? "text-navy-700 hover:bg-concrete-100" : "text-white hover:bg-white/10",
                        )}
                    >
                        <Phone className="w-5 h-5" />
                    </a>
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="header-mobile-toggle"
                                aria-label="Ouvrir le menu"
                                className={cn(
                                    "p-2 rounded transition",
                                    isFilled ? "text-navy-700 hover:bg-concrete-100" : "text-white hover:bg-white/10",
                                )}
                            >
                                <Menu className="w-6 h-6" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[320px] p-0 bg-white">
                            <div className="h-[80px] px-5 flex items-center justify-between border-b border-concrete-200">
                                <img src={SITE.logos.blue} alt="RCPS" className="h-9" />
                                <button
                                    aria-label="Fermer"
                                    onClick={() => setMobileOpen(false)}
                                    className="p-2 rounded hover:bg-concrete-100 text-navy-700"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <nav className="px-3 py-4 flex flex-col">
                                {NAV_ITEMS.map((item) =>
                                    item.children ? (
                                        <div key={item.label} className="mb-1">
                                            <button
                                                onClick={() => setMobilePresta((v) => !v)}
                                                className="w-full flex items-center justify-between px-3 py-3 rounded text-navy-800 font-medium hover:bg-concrete-100"
                                                data-testid="header-mobile-prestations-toggle"
                                            >
                                                {item.label}
                                                <ChevronDown
                                                    className={cn(
                                                        "w-4 h-4 transition-transform",
                                                        mobilePresta && "rotate-180",
                                                    )}
                                                />
                                            </button>
                                            {mobilePresta && (
                                                <div className="mt-1 ml-2 border-l border-concrete-200 pl-3">
                                                    {item.children.map((c) => (
                                                        <NavLink
                                                            key={c.to}
                                                            to={c.to}
                                                            className={({ isActive }) =>
                                                                cn(
                                                                    "block px-3 py-2 rounded text-sm text-navy-700",
                                                                    isActive && "bg-concrete-100",
                                                                )
                                                            }
                                                        >
                                                            {c.label}
                                                        </NavLink>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            end={item.to === "/"}
                                            className={({ isActive }) =>
                                                cn(
                                                    "px-3 py-3 rounded text-navy-800 font-medium",
                                                    isActive ? "bg-concrete-100" : "hover:bg-concrete-100",
                                                )
                                            }
                                        >
                                            {item.label}
                                        </NavLink>
                                    ),
                                )}
                                <div className="mt-4 px-3">
                                    <Link to="/contact">
                                        <Button className="w-full h-11 bg-navy-700 hover:bg-navy-800 text-white font-semibold">
                                            Demander un devis
                                        </Button>
                                    </Link>
                                    <a
                                        href={`tel:${SITE.phoneTel}`}
                                        className="mt-3 flex items-center justify-center gap-2 h-11 rounded border border-concrete-200 text-navy-700 font-medium"
                                    >
                                        <Phone className="w-4 h-4" /> {SITE.phone}
                                    </a>
                                    <a
                                        href={`tel:${SITE.phone2Tel}`}
                                        className="mt-2 flex items-center justify-center gap-2 h-11 rounded border border-concrete-200 text-navy-700 font-medium"
                                    >
                                        <Phone className="w-4 h-4" /> {SITE.phone2}
                                    </a>
                                </div>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
